import React, { useEffect, useReducer, useState } from "react";
import { Button } from "components/Buttons/Buttons";
import { Avatar, Dropdown, Menu, Modal, Select, Space, Table } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import useSettingsHeader from "hooks/useSettingsHeader";
import { useDispatch } from "react-redux";
import {
  getAllDeals,
  activateDeal,
  deactivateDeal,
} from "services/deals.service";
import { getAllCategories } from "services/categoriesSettings.services";
import SearchAndFilterColumns from "components/SearchAndFilterColumns/SearchAndFilterColumns";
import usePortal from "hooks/usePortal";
import DealsAddEditModal from "./components/DealsAddEditModal";
import { DEAL_DISCOUNT_TYPE, getEnumKeyByValue } from "utils/enums";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET":
      return action.data;
    default:
      return state;
  }
};

function Deals() {
  const [showAddEditModal, setShowAddEditModal] = useState();
  const [data, localDispatch] = useReducer(reducer, []);

  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [activeFilter, setActiveFilter] = useState("active"); // all, active, inactive
  const [searchText, setSearchText] = useState("");

  const [categories, setCategories] = useState({
    isLoading: true,
    data: [],
  });

  const dispatch = useDispatch();

  const loadData = async () => {
    const result = await dispatch(getAllDeals());
    if (result?.status === "success") {
      localDispatch({
        type: "SET",
        data: result.data,
      });
    }
  };

  useEffect(() => {
    loadData();
    dispatch(getAllCategories(false)).then((result) => {
      if (result?.status === "success") {
        setCategories({
          isLoading: false,
          data: result.data ?? [],
        });
      }
    });
  }, []);

  const handleToggleActive = (record) => {
    const action = record?.isActive ? deactivateDeal : activateDeal;
    dispatch(action(record?.dealId)).then((result) => {
      if (result?.status === "success") {
        loadData();
      }
    });
  };

  const checkActiveFilter = (item) => {
    if (activeFilter === "all") return true;
    else if (activeFilter === "active" && item?.isActive) return true;
    else if (activeFilter === "inactive" && !item?.isActive) return true;
    return false;
  };

  const inactivateConfirmation = (record) => ({
    title: "Inactivate Deal",
    content: (
      <>Are you sure that you want to inactivate {record?.headline ? `"${record.headline}"` : "this deal"}?</>
    ),
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      handleToggleActive(record);
      close();
    },
  });

  const activateConfirmation = (record) => ({
    title: "Activate Deal",
    content: (
      <>Are you sure that you want to activate {record?.headline ? `"${record.headline}"` : "this deal"}?</>
    ),
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      handleToggleActive(record);
      close();
    },
  });

  const settingHeaderContent = useSettingsHeader(
    <Button
      rounded={true}
      onClick={() => {
        setShowAddEditModal(true);
      }}
    >
      Create Deal
    </Button>
  );

  const columns = [
    {
      title: "Image",
      key: "imageUrl",
      width: 80,
      render: (record) => <Avatar shape="square" src={record?.imageUrl} />,
    },
    {
      title: "Headline",
      dataIndex: "headline",
      key: "headline",
      sorter: (a, b) => {
        if (a.headline?.toLowerCase?.() < b.headline?.toLowerCase?.()) return -1;
        if (a.headline?.toLowerCase?.() > b.headline?.toLowerCase?.()) return 1;
        return 0;
      },
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      sorter: (a, b) => {
        if (a.categoryName?.toLowerCase?.() < b.categoryName?.toLowerCase?.()) return -1;
        if (a.categoryName?.toLowerCase?.() > b.categoryName?.toLowerCase?.()) return 1;
        return 0;
      },
    },
    {
      title: "Discount Type",
      dataIndex: "discountType",
      key: "discountType",
      render: (value) => getEnumKeyByValue(DEAL_DISCOUNT_TYPE, value) ?? "-",
    },
    {
      title: "Discount Value",
      dataIndex: "discountValue",
      key: "discountValue",
      render: (value) => (value === null || value === undefined ? "-" : value),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
    },
    {
      title: "Active",
      key: "active",
      width: 100,
      render: (record) => (record?.isActive ? "Active" : "Inactive"),
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 80,
      render: (record) => {
        return (
          <Dropdown
            overlay={
              <Menu style={{ borderRadius: 6, border: "2px solid #CDD5D8 " }}>
                <Menu.Item onClick={() => setShowAddEditModal(record)} key="edit">
                  Edit
                </Menu.Item>
                <Menu.Item
                  key="toggle"
                  onClick={() => {
                    if (record?.isActive) Modal.warning(inactivateConfirmation(record));
                    else Modal.warning(activateConfirmation(record));
                  }}
                >
                  {record?.isActive ? "Inactivate" : "Activate"}
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            getPopupContainer={(triggerNode) => triggerNode?.parentNode}
          >
            <MoreOutlined style={{ fontSize: 18 }} />
          </Dropdown>
        );
      },
    },
  ];

  const searchAndFiltersContent = usePortal(
    "#settingHeader .settingsHeaderLeftSideAddon",
    <Space>
      <SearchAndFilterColumns
        columns={columns}
        hiddenColumns={hiddenColumns}
        setHiddenColumns={(columnTitles) => {
          setHiddenColumns(columnTitles);
        }}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
        }}
      />
      <Select
        style={{ width: 120 }}
        value={activeFilter}
        onChange={(value) => {
          setActiveFilter(value);
        }}
        options={[
          {
            value: "all",
            label: "All",
          },
          {
            value: "active",
            label: "Active",
          },
          {
            value: "inactive",
            label: "Inactive",
          },
        ]}
      />
    </Space>
  );

  return (
    <div>
      {settingHeaderContent}
      {searchAndFiltersContent}
      <Table
        rowKey={(data) => data.dealId}
        tableLayout="auto"
        locale={{ emptyText: "No Deals Found" }}
        dataSource={data?.filter(
          (it) =>
            checkActiveFilter(it) &&
            `${it?.headline ?? ""} ${it?.categoryName ?? ""}`
              .toLowerCase()
              .includes(searchText.toLowerCase())
        )}
        columns={columns.filter((column) => {
          if (!hiddenColumns.includes(column.title)) return true;
          return false;
        })}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [15, 25, 50, 100, 250],
        }}
      />

      <DealsAddEditModal
        data={showAddEditModal}
        categories={categories.data}
        isCategoriesLoading={categories.isLoading}
        visible={showAddEditModal}
        onCancel={() => {
          setShowAddEditModal();
        }}
        onSuccess={() => {
          setShowAddEditModal();
          loadData();
        }}
      />
    </div>
  );
}

export default Deals;
